/**
 * Integration connection state.
 *
 * An integration can be unavailable for reasons that are not errors. HeyReach's
 * subscription lapsed on 2026-06-26; every call since then fails authentication.
 * Rendering that as "Sign in required — your session has expired" to an operator
 * holding a perfectly valid session is worse than useless: it sends them to fix
 * the one thing that is not broken.
 *
 * `not_connected` is the honest third state between "working" and "broken", and
 * it is deliberately not retryable — retrying a lapsed subscription cannot help.
 */

export type IntegrationKey = 'heyreach' | 'trigify' | 'lgm';

// codex-review .contract-lens r9 F2 (O187): 'captured_only' is LGM's permanent state, not a
// transient outage — LGM has no API to fetch full conversation history at all, so a thread proven
// lgm-origin (conversation_source = 'lgm') can never become 'connected'/'degraded' for history
// purposes. It is deliberately distinct from 'not_connected' (which implies "reconnect it and this
// will work") — reconnecting cannot fix a capability that does not exist.
//
// codex-review .contract-lens r17 F3: derived from IntegrationState's own status literal, not
// redeclared independently — a second, hand-maintained copy of the same vocabulary is exactly how
// a status could be added to one and silently omitted from the other.
export type IntegrationStatus = IntegrationState['status'];

export type NotConnectedReason =
  | 'subscription_inactive'
  | 'provider_rejected_credentials'
  | 'not_configured'
  | 'provider_unreachable';

export type IntegrationReason = NotConnectedReason | 'lgm_no_history_api';

// codex-review .contract-lens r14 F3: IntegrationState previously declared key/status/reason as
// three INDEPENDENT fields, so TypeScript accepted nonsense tuples no producer builds today but
// nothing stopped a FUTURE one from compiling — e.g. { key: 'heyreach', status: 'captured_only',
// reason: 'subscription_inactive' } or { key: 'lgm', status: 'connected', reason:
// 'lgm_no_history_api' }. Discriminated on `status`: 'captured_only' is LGM-only (per the module
// comment above, LGM has no history API at all, ever) with its only valid reason being
// 'lgm_no_history_api'; 'not_connected' takes any NotConnectedReason regardless of key;
// 'connected'/'degraded' never carry a reason.
//
// codex-review .contract-lens r17 F3: the first two branches still used the FULL IntegrationKey,
// so { key: 'lgm', status: 'connected' } and { key: 'lgm', status: 'degraded' } compiled fine —
// contradicting this module's own comment that LGM can never be connected/degraded. Narrowed to
// 'heyreach' | 'trigify' — the only two keys any current producer ever pairs with these statuses
// (grep-confirmed repo-wide: 'lgm' appears ONLY in the captured_only branch anywhere in this repo).
export type IntegrationState =
  | { key: 'heyreach' | 'trigify'; status: 'connected'; since?: string }
  | { key: 'heyreach' | 'trigify'; status: 'degraded'; since?: string }
  | { key: 'heyreach' | 'trigify'; status: 'not_connected'; reason?: NotConnectedReason; since?: string }
  | { key: 'lgm'; status: 'captured_only'; reason?: 'lgm_no_history_api'; since?: string };

interface IntegrationCopy {
  label: string;
  description: string;
  action: string;
}

const REASON_COPY: Record<IntegrationReason, string> = {
  subscription_inactive:
    'The subscription for this integration is not active.',
  provider_rejected_credentials:
    'The provider rejected the stored credentials.',
  not_configured:
    'This integration has not been configured yet.',
  provider_unreachable:
    'The provider could not be reached.',
  lgm_no_history_api:
    'LGM has no API for retrieving full conversation history.',
};

const KEY_LABEL: Record<IntegrationKey, string> = {
  heyreach: 'LinkedIn (HeyReach)',
  trigify: 'Trigify',
  lgm: 'LinkedIn (LGM)',
};

/**
 * User-facing copy for an integration state. English only: this is interface
 * chrome, not payload, and the English-consistency scanner reads it.
 */
export const describeIntegrationState = (state: IntegrationState): IntegrationCopy => {
  const name = KEY_LABEL[state.key];

  if (state.status === 'connected') {
    return {
      label: 'Connected',
      description: `${name} is connected and responding.`,
      action: '',
    };
  }

  if (state.status === 'degraded') {
    return {
      label: 'Degraded',
      description: `${name} is responding slowly or intermittently.`,
      action: 'No action needed right now.',
    };
  }

  if (state.status === 'captured_only') {
    return {
      label: 'Captured only',
      description: state.reason
        ? `${name} is captured only. ${REASON_COPY[state.reason]}`
        : `${name} is captured only — full history is unavailable.`,
      action: 'No action needed — messages already captured are shown below.',
    };
  }

  return {
    label: 'Not connected',
    description: state.reason
      ? `${name} is not connected. ${REASON_COPY[state.reason]}`
      : `${name} is not connected.`,
    action: 'Reconnect it in Settings when the subscription is active again.',
  };
};

export const isIntegrationUsable = (state: IntegrationState | undefined): boolean =>
  state === undefined || state.status !== 'not_connected';

/**
 * Server-side gate. An integration counts as connected only when it is explicitly
 * marked connected AND its credential is present. Defaulting to "connected" when
 * the flag is absent would reintroduce the failure this module exists to prevent.
 */
export const readIntegrationFlag = (
  connectedFlag: string | undefined,
  apiKey: string | undefined,
): { connected: boolean; reason?: NotConnectedReason } => {
  if (!apiKey) {
    return { connected: false, reason: 'not_configured' };
  }
  if (connectedFlag !== 'true') {
    return { connected: false, reason: 'subscription_inactive' };
  }
  return { connected: true };
};
